import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

export default function AddCompany() {
  const navigate = useNavigate();

  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [logoFile, setLogoFile] = useState(null);

  const [formData, setFormData] = useState({
    companyName: "",
    address: "",
    telephone: "",
    email: "",
    website: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileChange = (e) => {
    setLogoFile(e.target.files[0]);
  };

  const validate = () => {
    const requiredFields = [
      "companyName",
      "address",
      "telephone",
      "email",
      "website"
    ];

    const newErrors = {};

    requiredFields.forEach((field) => {
      if (!formData[field] || formData[field].trim() === "") {
        newErrors[field] = `${field.replace(/([A-Z])/g, ' $1').toLowerCase()} is required`;
      }
    });

    // Email validation
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    // Phone validation (basic)
    if (formData.telephone && !/^[\+]?[0-9\s\-\(\)]+$/.test(formData.telephone)) {
      newErrors.telephone = "Please enter a valid phone number";
    }

    // Website validation (basic)
    if (formData.website && !/^[a-zA-Z0-9][a-zA-Z0-9-]{1,61}[a-zA-Z0-9]\.[a-zA-Z]{2,}$/.test(formData.website)) {
      newErrors.website = "Please enter a valid website (e.g., example.com)";
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setSubmitted(false);
      return;
    }

    try {

      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 1000));

      // For now, just show success message
      Swal.fire("Success", "Company added successfully!", "success");
      navigate("/Admin/Insurance");

    } catch (error) {
      Swal.fire("Error", "Failed to add company. Please try again.", "error");
      console.error(error);
    }
  };

  return (
    <div className="pc-container">
      <div className="pc-content">
        <div className="col-12">
          <div className="card table-card">
            <div className="tableHeader">
              <h5 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span
                  style={{ cursor: 'pointer', color: '#3f75c7', fontWeight: 'bold', fontSize: '1.5rem' }}
                  onClick={() => navigate(-1)}
                  title="Back"
                >
                  &larr;
                </span>
                Add Company
              </h5>
            </div>

            {submitted && (
              <div className="alert alert-success">
                Company added successfully!
              </div>
            )}

            <form
              onSubmit={handleSubmit}
              encType="multipart/form-data"
              className="row g-3 px-3 py-2 mb-3"
            >
              {/* Company Name */}
              <div className="col-md-6">
                <label className="form-label">Company Name *</label>
                <input
                  type="text"
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleChange}
                  className="form-control"
                  placeholder="Enter company name"
                />
                {errors && errors.companyName && (
                  <p className="text-danger">{errors.companyName}</p>
                )}
              </div>

              {/* Address */}
              <div className="col-md-6">
                <label className="form-label">Address *</label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  className="form-control"
                  placeholder="Enter company address"
                />
                {errors && errors.address && (
                  <p className="text-danger">{errors.address}</p>
                )}
              </div>

              {/* Telephone */}
              <div className="col-md-6">
                <label className="form-label">Phone *</label>
                <input
                  type="tel"
                  name="telephone"
                  value={formData.telephone}
                  onChange={handleChange}
                  className="form-control"
                  placeholder="Enter phone number (e.g., +973 1234 5678)"
                />
                {errors && errors.telephone && (
                  <p className="text-danger">{errors.telephone}</p>
                )}
              </div>

              {/* Email */}
              <div className="col-md-6">
                <label className="form-label">Email *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="form-control"
                  placeholder="Enter email address"
                />
                {errors && errors.email && (
                  <p className="text-danger">{errors.email}</p>
                )}
              </div>

              {/* Website */}
              <div className="col-md-6">
                <label className="form-label">Website *</label>
                <input
                  type="text"
                  name="website"
                  value={formData.website}
                  onChange={handleChange}
                  className="form-control"
                  placeholder="Enter website (e.g., example.com)"
                />
                {errors && errors.website && (
                  <p className="text-danger">{errors.website}</p>
                )}
              </div>

              {/* Logo Upload */}
              <div className="col-md-6">
                <label className="form-label">Company Logo</label>
                <input
                  type="file"
                  name="logo"
                  onChange={handleFileChange}
                  className="form-control"
                  accept="image/*"
                />
                <small className="form-text text-muted">
                  Upload company logo (JPG, PNG, GIF - Max 5MB)
                </small>
                {logoFile && (
                  <div className="mt-2">
                    <small className="text-success">
                      Selected: {logoFile.name}
                    </small>
                  </div>
                )}
              </div>

              {/* Submit Buttons */}
              <div className="col-12">
                <div className="d-flex gap-2">
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={submitted}
                  >
                    {submitted ? "Adding..." : "Add Company"}
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => navigate(-1)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
















